import { ref, computed, watch } from 'vue'
import { isEqual } from 'lodash'
import { useConnector } from '../../../composables/useConnector'
import { connectInfiniteHits } from '@/helpers/instantSearchConnectors/connectInfiniteHits'

const getStateWithoutPage = ({ page, ...rest } = {}) => rest

/**
 * Bridges connectInfiniteHits to DataGrid's flat rows array.
 * Mirrors useVirtualHitsPageProvider but flattens all cached pages into one array.
 */
export const useInfiniteHits = ({ fallbackPerPage = 40 } = {}) => {
  const cachedHits = ref(null)
  const cachedState = ref(null)

  const cache = {
    read: ({ state }) =>
      isEqual(cachedState.value, getStateWithoutPage(state)) ? cachedHits.value : null,
    write: ({ state, hits }) => {
      cachedState.value = getStateWithoutPage(state)
      cachedHits.value = { ...hits }
    }
  }

  const state = useConnector(connectInfiniteHits, {
    $$widgetType: 'ais.infiniteHits',
    cache
  })

  const total = computed(() => state.value?.nbHits ?? 0)
  const pageSize = computed(() => state.value?.results?.hitsPerPage ?? fallbackPerPage)
  const isPending = computed(() => !state.value)

  const rows = computed(() => {
    if (!cachedHits.value) return []
    return Object.keys(cachedHits.value)
      .map(Number)
      .sort((a, b) => a - b)
      .flatMap(page => cachedHits.value[page])
  })

  const loadNextPage = () => {
    if (!state.value || state.value.isLastPage) return
    const pagesLoaded = Object.keys(cachedHits.value ?? {}).length
    state.value.showMore([pagesLoaded])
  }

  // resetKey bumps on query/refinement change so DataGrid can reset virtualizer
  const resetKey = ref(0)
  let prevState = null
  watch(cachedState, newState => {
    if (prevState !== null && !isEqual(newState, prevState)) {
      resetKey.value++
    }
    prevState = newState
  })

  return { rows, total, pageSize, isPending, loadNextPage, resetKey }
}
